import { Component, ViewEncapsulation, ViewChild, OnInit, OnDestroy, Inject } from '@angular/core';
import { HeaderComponent } from '../header/header.component';
import { NavigationEnd, Router } from '@angular/router';
import { Subject } from 'rxjs';
import { filter, takeUntil } from 'rxjs/operators';
import { StoresManager } from 'src/app/_global/stores-manager';
import { HelperService } from 'src/app/_services/helper.service';
import { environment } from 'src/environments/environment';
import { Store } from '@ngxs/store';
import { UserActions } from 'src/app/states/user/user.actions';
import { AppConfig, APP_CONFIG } from 'src/environments/app.config';
import { StatesHelper } from 'src/app/states/states.helper';
import { OrdersService } from 'src/app/_services/orders.service';
import { MessageHubService } from 'src/app/_services/message-hub.service';
import { DateRange } from 'src/app/_models/self-ship/orders/orders-filters.model';
import { OrdersFetchFilters } from 'src/app/_services/self-ship/self-ship-orders.service';

@Component({
	selector: 'kt-base',
	templateUrl: './base.component.html',
	styleUrls: ['./base.component.scss'],
	encapsulation: ViewEncapsulation.None
})
export class BaseComponent implements OnInit, OnDestroy {

	appVersion: string;
	environment = environment;
	year: number = new Date().getFullYear();
	expendBar: boolean = false;
	@ViewChild(HeaderComponent, { static: true }) header: HeaderComponent;
	userFullName: string;
	openOrdersCount: number = 0;
	messagesNeedingResponse: number = 0;
	private readonly destroy$ = new Subject<void>();
	hideFooterPages: string[] = ["/self-ship-orders/orders", "/fba-shipments"];
	hideFooter: boolean = false;

	constructor(
		private router: Router,
		public stores: StoresManager,
		public helperService: HelperService,
		private stateStore: Store,
		private ordersService: OrdersService,
		private messageHubService: MessageHubService,
		@Inject(APP_CONFIG) appConfig: AppConfig,
		private statesHelper: StatesHelper, /* Required to instantiate. Do not remove */
	) {
		this.appVersion = appConfig?.version;
		router.events.subscribe(r => this.header.title = undefined);
	}

	ngOnInit(): void {
		const user = this.helperService.currentUser;
		if (user) {
			this.userFullName = `${user.firstName} ${user.lastName}`;
		}

		this.checkToHideFooter(this.router.url);
		this.router.events.subscribe(event => {
			if (event instanceof NavigationEnd) {
				this.checkToHideFooter(event.url);
			}
		});

		if (this.helperService.isLoggedIn() && this.helperService.hasPermission(['SelfShipOrders'])) {
			this.refreshOpenOrdersCount();
			this.router.events.pipe(
				filter(event => event instanceof NavigationEnd && event.urlAfterRedirects.includes('/self-ship-orders')),
				takeUntil(this.destroy$)
			).subscribe(() => this.refreshOpenOrdersCount());
		}

		if (this.helperService.isLoggedIn() && this.helperService.hasPermission(['Messages'])) {
			this.refreshMessagesNeedingResponse();
			this.router.events.pipe(
				filter(event => event instanceof NavigationEnd && event.urlAfterRedirects.includes('/messages')),
				takeUntil(this.destroy$)
			).subscribe(() => this.refreshMessagesNeedingResponse());
		}
	}

	ngOnDestroy(): void {
		this.destroy$.next();
		this.destroy$.complete();
	}

	// Deliberately independent of the merchant-orders page filter state, so the
	// global nav badge always reflects the default (last 30 days, unfiltered) count.
	private refreshOpenOrdersCount() {
		const dateRange = new DateRange();
		const filters = {
			fromDate: dateRange.fromDate.toUTCString(),
			toDate: dateRange.toDate.toUTCString()
		} as OrdersFetchFilters;
		this.ordersService.getTotalOrdersCount(filters)
			.pipe(takeUntil(this.destroy$))
			.subscribe(orderCount => this.openOrdersCount = orderCount?.open ?? 0);
	}

	// The badge answers "do buyers need me?" — so it counts threads waiting on a reply, the same
	// number the inbox's Response needed tab shows. One thread costs nothing to fetch; the counts
	// ride along with the list response.
	private refreshMessagesNeedingResponse() {
		this.messageHubService.listThreads({ take: 1 })
			.pipe(takeUntil(this.destroy$))
			.subscribe(response => this.messagesNeedingResponse = response?.metrics?.needsResponse ?? 0);
	}

	checkToHideFooter(url: string) {
		this.hideFooter = this.hideFooterPages.some(pageUrl => url.includes(pageUrl));
	}

	barClick(event: boolean) {
		if (event) {
			this.expendBar = false;
		} else {
			this.expendBar = true;
		}
	}



	signOut() {
		this.stateStore.dispatch(new UserActions.Logout).subscribe(_ => window.location.href = '');
	}

	getMessage(subscriptionStatus: string) {
		const inactiveDays = this.helperService.inactiveDays;
		const accountWillBeLockedIn = Math.abs(7 - this.helperService.inactiveDays);

		let message = '';
		if (inactiveDays > 0 && inactiveDays < 7) {
			message = `Your account will be disabled in ${accountWillBeLockedIn} days `;
		}
		if (inactiveDays == 7) {
			message = 'Your account will be disabled by the end of the day ';
		}
		if (inactiveDays > 7) {
			message = 'Your account has been disabled ';
		}

		switch (subscriptionStatus) {
			case 'incomplete':
			case 'incomplete_expired':
			case 'incomplete':
				return message + 'because your subscription is incomplete or the card was declined.';
			case 'past_due':
				return message + 'because your subscription is past due.';
			case 'canceled':
				return message + 'because your subscription has been canceled.';
			case 'unpaid':
				return message + 'because your subscription is unpaid.';
			default:
				return message + 'because your subscription has been canceled.';
		}
	}

	getMessageForExceeded() {
		const exceededDays = this.helperService.exceededDays;
		const accountWillBeLockedIn = Math.abs(7 - exceededDays);

		let message = '';
		if (exceededDays > 0 && exceededDays < 7) {
			message = `Your account has reached the maximum limit of products or monthly orders. Your account will be locked in ${accountWillBeLockedIn} days. `;
		}
		if (exceededDays == 7) {
			message = 'Your account has reached the maximum limit of products or monthly orders over 7 days ago. And will be locked by the end of the day ';
		}
		if (exceededDays > 7) {
			message = 'Your account has reached the maximum limit of products or monthly orders over 7 days ago. ';
		}
		return message;
	}
}

